import { Card, Icon } from "@blueprintjs/core";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { CatalogueObject, CatalogueOrbitClass } from "../../types/catalog";
import { getDisplayObjectValue } from "../../utils/catalogObject";

const ORBIT_CLASS_COLORS: Record<CatalogueOrbitClass, string> = {
  GTO: "#9333ea",
  LEO: "#0f766e",
  MEO: "#2563eb",
  GEO: "#c2410c",
  UNKNOWN: "#71717a",
  LLEO: "#0cc27c",
  OTH: "#eb259c",
};

const DetailValue = ({ label, value }: { label: string; value: string }) => (
  <div className="min-w-0 rounded-md bg-slate-50 px-3 py-2">
    <div className="truncate text-[11px] font-semibold uppercase text-slate-500">
      {label}
    </div>
    <div className="mt-1 break-words text-sm font-semibold text-slate-950">
      {value || "-"}
    </div>
  </div>
);

export const CatalogObjectCard = ({
  dragHandle,
  object,
  onContactsSelect,
  onTogglePinned,
  pinned,
  showDetailsLink = true,
  showPinControl = true,
}: {
  object: CatalogueObject;
  pinned: boolean;
  dragHandle?: ReactNode;
  onContactsSelect?: (organization: string, object: CatalogueObject) => void;
  onTogglePinned?: (object: CatalogueObject) => void;
  showDetailsLink?: boolean;
  showPinControl?: boolean;
}) => {
  const id = object.id;

  const name = getDisplayObjectValue(object.name);
  const noradId = getDisplayObjectValue(object.noradId);
  const cosparId = getDisplayObjectValue(object.cosparId);
  const organization = getDisplayObjectValue(object.operator);
  const orbitClass = getDisplayObjectValue(
    object.regime,
  ) as CatalogueOrbitClass;
  const objectType = getDisplayObjectValue(object.typeOfObject);
  const status = getDisplayObjectValue(object.status);

  return (
    <Card className="rounded-2xl! border border-slate-200 shadow-sm transition-all duration-300 ease-out">
      <article className="flex flex-col gap-4">
        <header className="flex gap-3">
          {dragHandle}
          <div className="flex min-w-0 flex-1 items-start justify-between gap-3">
            <div className="min-w-0 flex gap-3">
              <div className="min-w-0">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <h3 className="m-0 truncate text-lg font-bold text-slate-950">
                    {name}
                  </h3>
                  <span
                    className="rounded-full px-2.5 py-1 text-xs font-bold text-white"
                    style={{
                      backgroundColor:
                        ORBIT_CLASS_COLORS[orbitClass] ??
                        ORBIT_CLASS_COLORS.UNKNOWN,
                    }}
                  >
                    {orbitClass}
                  </span>
                  {pinned && (
                    <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-700 ring-1 ring-blue-200">
                      Pinned
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                  {(noradId || cosparId) && (
                    <span className="flex gap-1">
                      {noradId && <span>{noradId}</span>}
                      {noradId && cosparId && <span>|</span>}
                      {cosparId && <span>{cosparId}</span>}
                    </span>
                  )}
                  {organization && <span>{organization}</span>}
                </div>
              </div>
              <DetailValue
                label="Type / status"
                value={[objectType, status].filter(Boolean).join(" - ")}
              />
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {showDetailsLink && (
                <Link
                  aria-label={`View details for ${name}`}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white !text-slate-500 no-underline transition hover:border-slate-300 hover:bg-slate-50 hover:!text-slate-800 hover:no-underline focus:!text-slate-800 focus:no-underline"
                  to={`/catalogue/${id}/info`}
                >
                  <Icon icon="eye-open" size={16} />
                </Link>
              )}
              {onContactsSelect && (
                <button
                  aria-label={
                    organization
                      ? `Show ${organization} contacts`
                      : `No organization contacts available for ${name}`
                  }
                  className={`inline-flex h-9 w-9 items-center justify-center rounded-full border transition ${
                    organization
                      ? "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
                      : "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-300"
                  }`}
                  disabled={!organization}
                  onClick={() => {
                    if (organization) {
                      onContactsSelect(organization, object);
                    }
                  }}
                  title={
                    organization
                      ? `Show ${organization} contacts`
                      : "No organization contacts available"
                  }
                  type="button"
                >
                  <Icon icon="book" size={16} />
                </button>
              )}
              {showPinControl && (
                <button
                  aria-label={`${pinned ? "Unpin" : "Pin"} ${name}`}
                  aria-pressed={pinned}
                  className={`inline-flex h-9 w-9 items-center justify-center rounded-full border transition ${
                    pinned
                      ? "border-blue-200 bg-blue-50 text-blue-700"
                      : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
                  }`}
                  onClick={() => onTogglePinned?.(object)}
                  type="button"
                >
                  <Icon icon="pin" size={16} />
                </button>
              )}
            </div>
          </div>
        </header>
      </article>
    </Card>
  );
};
